import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { hojeBrasilia, lerDevolucoes } from '../../central';
import type { Devolucoes } from '../../central';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import { agrupar } from '../../design/graficos/agrupar';
import {
  MODELOS_PARTES,
  MODELOS_TEMPO,
  fmt,
  partes,
  serieNoTempo,
} from '../../design/graficos/modelos';
import { reais } from '../../produtos';
import { hora } from '../../vendas';
import { BarraDeFiltros, Kpi, irPara, lerFiltros, textoDoPeriodo } from '../paineis/pecasDaVisao';
import { SubnavDeVendas } from './SubnavDeVendas';
import './central.css';
import { linhaClicavel } from '../../design/linha-clicavel';

/**
 * TELA 14C — DEVOLUÇÕES E CANCELAMENTOS NO CAIXA (abas separadas, números próprios, nunca somados).
 * Devolução: o cliente trouxe de volta — vem da API do HOS com o cupom da venda original, quem
 * vendeu e quem recebeu; tira da premiação pela regra do cheque. Cancelamento no caixa: o item (ou o
 * cupom inteiro) cancelado na hora da venda, com quem cancelou. A fonte não traz motivo de nenhum
 * dos dois, e o item cancelado não tem valor — a tela diz isso, nunca inventa.
 */

type Aba = 'devolucoes' | 'cancelamentos';
const menos = (v: string) => `−${reais(v.replace('-', ''))}`;
const diaCurto = (s: string) => s.split('-').reverse().slice(0, 2).join('/');
const primeiroNome = (s: string) => {
  const p = s.trim().split(/\s+/)[0] ?? s;
  return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
};

export function DevolucoesECancelamentos({ parametros }: { parametros: URLSearchParams }) {
  const hoje = hojeBrasilia();
  // padrão desta tela: o mês (devolução é rara num dia só)
  const q = new URLSearchParams(parametros);
  if (!q.get('p') && !q.get('dia')) q.set('p', 'mes');
  const f = lerFiltros(q, hoje);
  const { de, ate } = f.periodo;
  const aba: Aba = parametros.get('aba') === 'cancelamentos' ? 'cancelamentos' : 'devolucoes';
  const vendedor = /^\d{1,10}$/.test(parametros.get('vendedor') ?? '')
    ? parametros.get('vendedor')
    : null;
  const extras = { aba: aba === 'cancelamentos' ? aba : null, vendedor };
  const ir = (mudar: Partial<typeof extras>) =>
    irPara({ filtro: f.filtro, periodo: f.periodo, pessoa: null }, 'devolucoes', {
      ...extras,
      ...mudar,
    });
  const [sel, setSel] = useState(0);
  const [d, setD] = useState<Devolucoes | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    setSel(0);
    void lerDevolucoes(de, ate, vendedor).then((r) => (r.ok ? setD(r.dados) : setErro(true)));
  }, [de, ate, vendedor]);
  useEffect(carregar, [carregar]);

  const quando = textoDoPeriodo(f.filtro, f.periodo, hoje);
  const r = d?.resumo;
  const nomeDoVendedor = d?.balconistas.find((b) => b.codigo === vendedor)?.nome;

  return (
    <section className="cv">
      <SubnavDeVendas atual="devolucoes" direita="API do HOS" />
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <h1>Devoluções e cancelamentos</h1>
            <div className="vg-contexto">
              Duas coisas diferentes, nunca somadas · {quando}
              {nomeDoVendedor ? ` · ${nomeDoVendedor}` : ''}
            </div>
          </div>
        </header>

        <BarraDeFiltros f={f} hoje={hoje} rota="devolucoes" extras={extras}>
          <label className="vg-pessoa">
            <Icone nome="users" tamanho={14} traco={1.6} />
            <select
              value={vendedor ?? ''}
              aria-label="Balconista"
              onChange={(e) => ir({ vendedor: e.target.value || null })}
            >
              <option value="">Todos os balconistas</option>
              {(d?.balconistas ?? []).map((b) => (
                <option key={b.codigo} value={b.codigo}>
                  {b.nome} · {b.codigo}
                </option>
              ))}
            </select>
          </label>
        </BarraDeFiltros>

        <div className="vg-segmentos cv-abas" role="tablist" aria-label="O que ver">
          <button
            type="button"
            role="tab"
            aria-selected={aba === 'devolucoes'}
            className={`vg-seg${aba === 'devolucoes' ? ' vg-seg-ativo' : ''}`}
            onClick={() => ir({ aba: null })}
          >
            Devoluções{r ? <em className="cv-seg-num">{fmt(r.devolucoes, 'inteiro')}</em> : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={aba === 'cancelamentos'}
            className={`vg-seg${aba === 'cancelamentos' ? ' vg-seg-ativo' : ''}`}
            onClick={() => ir({ aba: 'cancelamentos' })}
          >
            Cancelamentos no caixa
            {r ? (
              <em className="cv-seg-num">
                {fmt(r.itensCancelados + r.cuponsCancelados, 'inteiro')}
              </em>
            ) : null}
          </button>
        </div>

        {erro ? (
          <ErroDeArea oQue="as devoluções" aoTentar={carregar} />
        ) : aba === 'devolucoes' ? (
          <>
            <div className="vg-kpis cv-kpis-5">
              <Kpi
                rotulo={`Devoluções · ${quando}`}
                icone="arrow-l"
                valor={r ? fmt(r.devolucoes, 'inteiro') : null}
                variacao={null}
                sub={r ? `itens devolvidos em ${fmt(r.cuponsDevolvidos, 'inteiro')} cupons` : null}
                trilha={d?.devolucoesPorDia.map((x) => x.eventos)}
                link="#/devolucoes"
                destaque
              />
              <Kpi
                rotulo="Valor devolvido"
                icone="money"
                valor={r ? fmt(Number(r.valorDevolvido), 'reais') : null}
                variacao={null}
                sub={r ? 'quantidade × preço da venda original' : null}
                trilha={d?.devolucoesPorDia.map((x) => x.valor)}
                link="#/devolucoes"
              />
              <Kpi
                rotulo="Taxa de devolução"
                icone="gauge"
                valor={r ? (r.taxa.disponivel ? `${r.taxa.valor.replace('.', ',')}%` : '—') : null}
                variacao={null}
                sub={r ? (r.taxa.disponivel ? r.taxa.fonte : r.taxa.porque) : null}
                link="#/devolucoes"
              />
              <Kpi
                rotulo="Tirado da premiação"
                icone="trophy"
                valor={r ? fmt(Number(r.descontadoDaPremiacao), 'reais') : null}
                variacao={null}
                sub={r ? 'itens com prêmio que voltaram (regra do cheque)' : null}
                link="#/premiacao"
              />
              <Kpi
                rotulo="Sem motivo"
                icone="warn"
                valor={r ? fmt(r.semMotivo, 'inteiro') : null}
                variacao={null}
                sub={r ? 'a API do HOS não traz o motivo' : null}
                link="#/devolucoes"
              />
            </div>

            {d && (
              <div className="vg-linha cv-linha-2">
                <DevolucoesNoTempo d={d} />
                <PorBalconista
                  id="cv-dev-balconista"
                  titulo="Devoluções por balconista"
                  grupos={d.devolucoesPorBalconista}
                  formato="reais"
                  valor={(g) => g.valor}
                  frase={(g, total) => (
                    <>
                      <strong>{g.nome}</strong> teve {fmt(g.eventos, 'inteiro')} devoluções (
                      {fmt(g.valor, 'reais')}), {fmt((g.valor / total) * 100, 'pct')} do valor
                      devolvido. Quem vendeu o item, não quem recebeu.
                    </>
                  )}
                />
              </div>
            )}

            <div className="cv-com-lado">
              <div className="vg-bloco cv-lista">
                {!d ? (
                  <div className="vg-lista-osso" aria-busy="true">
                    {[0, 1, 2, 3, 4].map((k) => (
                      <i key={k} />
                    ))}
                  </div>
                ) : d.devolucoes.length === 0 ? (
                  <p className="cv-vazio">Nenhuma devolução no período.</p>
                ) : (
                  <div className="cv-rolagem">
                    <table className="cv-tabela">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Cupom orig.</th>
                          <th>Produto</th>
                          <th className="cv-dir">Qtd</th>
                          <th className="cv-dir">Valor</th>
                          <th>Vendeu</th>
                          <th>Recebeu</th>
                          <th className="cv-dir">Na premiação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.devolucoes.map((x, k) => (
                          <tr
                            key={`${x.cupom}-${x.cod}-${x.dia}`}
                            className={`cv-clicavel ${k === sel ? 'cv-linha-sel' : ''}`}
                            {...linhaClicavel(() => setSel(k))}
                          >
                            <td className="cv-mono cv-cinza">{diaCurto(x.dia)}</td>
                            <td className="cv-mono">{x.cupom}</td>
                            <td className="cv-forte">{x.produto}</td>
                            <td className="cv-dir">{x.quantidade}</td>
                            <td className="cv-dir cv-forte cv-neg">{menos(x.valor)}</td>
                            <td>{x.balconista}</td>
                            <td className={x.recebidaPor ? '' : 'cv-cinza'}>
                              {x.recebidaPor ?? '—'}
                            </td>
                            <td className="cv-dir">
                              {x.descontoNaPremiacao ? (
                                menos(x.descontoNaPremiacao)
                              ) : (
                                <span className="cv-cinza">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="cv-paginacao">
                  <span>{d ? `${fmt(d.devolucoes.length, 'inteiro')} devoluções` : ''}</span>
                  <span>
                    devolução não é erro por padrão · desconto na premiação pela regra do cheque
                  </span>
                </div>
              </div>
              {d && d.devolucoes[sel] && <LinhaDaDevolucao x={d.devolucoes[sel]} />}
            </div>
          </>
        ) : (
          <>
            <div className="vg-kpis cv-kpis-4">
              <Kpi
                rotulo={`Itens cancelados · ${quando}`}
                icone="x"
                valor={r ? fmt(r.itensCancelados, 'inteiro') : null}
                variacao={null}
                sub={r ? 'item tirado do cupom no caixa' : null}
                trilha={d?.cancelamentosPorDia.map((x) => x.itens)}
                link="#/devolucoes?aba=cancelamentos"
                destaque
              />
              <Kpi
                rotulo="Cupons inteiros cancelados"
                icone="receipt"
                valor={r ? fmt(r.cuponsCancelados, 'inteiro') : null}
                variacao={null}
                sub={
                  r
                    ? Number(r.valorCuponsCancelados)
                      ? `${fmt(Number(r.valorCuponsCancelados), 'reais')} nesses cupons`
                      : 'o cupom some das vendas do HOS'
                    : null
                }
                trilha={d?.cancelamentosPorDia.map((x) => x.cupons)}
                link="#/devolucoes?aba=cancelamentos"
              />
              <Kpi
                rotulo="Por dia"
                icone="cal"
                valor={
                  d
                    ? fmt(
                        (r?.itensCancelados ?? 0) / Math.max(1, d.cancelamentosPorDia.length),
                        'decimal',
                      )
                    : null
                }
                variacao={null}
                sub={d ? 'itens cancelados, em média' : null}
                link="#/devolucoes?aba=cancelamentos"
              />
              <Kpi
                rotulo="Quem mais cancela"
                icone="users"
                valor={
                  d
                    ? d.cancelamentosPorBalconista[0]
                      ? primeiroNome(d.cancelamentosPorBalconista[0].nome)
                      : '—'
                    : null
                }
                variacao={null}
                sub={
                  d?.cancelamentosPorBalconista[0]
                    ? `${fmt(d.cancelamentosPorBalconista[0].eventos, 'inteiro')} cancelamentos`
                    : null
                }
                link="#/devolucoes?aba=cancelamentos"
              />
            </div>

            {d && (
              <div className="vg-linha cv-linha-2">
                <CancelamentosNoTempo d={d} />
                <PorBalconista
                  id="cv-canc-balconista"
                  titulo="Quem cancela no caixa"
                  grupos={d.cancelamentosPorBalconista}
                  formato="inteiro"
                  valor={(g) => g.eventos}
                  frase={(g, total) => (
                    <>
                      <strong>{g.nome}</strong> fez {fmt(g.eventos, 'inteiro')} cancelamentos,{' '}
                      {fmt((g.eventos / total) * 100, 'pct')} do total. Muito acima dos outros é
                      conversa, não acusação: pode ser o caixa mais movimentado.
                    </>
                  )}
                />
              </div>
            )}

            <div className="vg-bloco cv-lista">
              {!d ? (
                <div className="vg-lista-osso" aria-busy="true">
                  {[0, 1, 2, 3, 4].map((k) => (
                    <i key={k} />
                  ))}
                </div>
              ) : d.cancelamentos.length === 0 ? (
                <p className="cv-vazio">Nenhum cancelamento no caixa no período.</p>
              ) : (
                <div className="cv-rolagem">
                  <table className="cv-tabela">
                    <thead>
                      <tr>
                        <th>Quando</th>
                        <th>Cupom</th>
                        <th>O quê</th>
                        <th>Balconista</th>
                        <th>Produto</th>
                        <th className="cv-dir">Qtd</th>
                        <th className="cv-dir">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.cancelamentos.map((c) => (
                        <tr
                          key={c.id}
                          className={c.saleId ? 'cv-clicavel' : ''}
                          {...(c.saleId
                            ? linhaClicavel(
                                () => (window.location.hash = `#/cupom?id=${c.saleId ?? ''}`),
                              )
                            : {})}
                        >
                          <td className="cv-mono cv-cinza">
                            {new Date(c.em).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              timeZone: 'America/Sao_Paulo',
                            })}{' '}
                            {hora(c.em)}
                          </td>
                          <td className="cv-mono">{c.cupom}</td>
                          <td>
                            <span
                              className={`cv-tipo cv-tipo-${c.tipo === 'cupom' ? 'cancelamento' : 'devolucao'}`}
                            >
                              {c.tipo === 'cupom' ? 'CUPOM' : 'ITEM'}
                            </span>
                          </td>
                          <td>{c.balconista ?? <span className="cv-cinza">não informado</span>}</td>
                          <td className="cv-forte">
                            {c.produto ?? <span className="cv-cinza">cupom inteiro</span>}
                          </td>
                          <td className="cv-dir">{c.quantidade ?? '—'}</td>
                          <td className="cv-dir">
                            {c.valor ? (
                              <span className="cv-neg cv-forte">{menos(c.valor)}</span>
                            ) : (
                              <span
                                className="cv-cinza"
                                title="o HOS não manda o valor do item cancelado"
                              >
                                —
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="cv-paginacao">
                <span>
                  {d && r
                    ? d.cancelamentos.length < r.itensCancelados + r.cuponsCancelados
                      ? `os ${fmt(d.cancelamentos.length, 'inteiro')} mais recentes de ${fmt(r.itensCancelados + r.cuponsCancelados, 'inteiro')} — filtre por balconista ou período`
                      : `${fmt(d.cancelamentos.length, 'inteiro')} cancelamentos`
                    : ''}
                </span>
                <span>o HOS não manda o valor do item cancelado nem o motivo</span>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function DevolucoesNoTempo({ d }: { d: Devolucoes }) {
  const blocos = agrupar(
    d.devolucoesPorDia.map((x) => ({
      dia: x.dia,
      faturamento: x.valor,
      cupons: x.eventos,
      itens: 0,
    })),
  );
  const total = d.devolucoesPorDia.reduce((s, x) => s + x.valor, 0);
  const pior = [...blocos].sort((a, b) => b.faturamento - a.faturamento)[0];
  return (
    <CartaoDeGrafico
      id="cv-dev-tempo"
      titulo="Devoluções no tempo"
      numero={fmt(total, 'reais')}
      conclusao={
        pior && pior.faturamento ? (
          <>
            O maior valor devolvido foi em <strong>{pior.rotulo}</strong> (
            {fmt(pior.faturamento, 'reais')} em {fmt(pior.cupons, 'inteiro')} devoluções).
          </>
        ) : (
          'Nenhuma devolução no período.'
        )
      }
      modelos={MODELOS_TEMPO}
      padrao="combo"
      desenhar={(m) =>
        serieNoTempo(m, {
          cats: blocos.map((b) => b.rotulo),
          atual: blocos.map((b) => b.faturamento),
          anterior: blocos.map(() => null),
          nome: 'Valor devolvido',
          formato: 'reais',
          linha: { nome: 'Devoluções', valores: blocos.map((b) => b.cupons), formato: 'inteiro' },
        })
      }
      fonte="Fonte: devoluções da API do HOS"
      vazio={total ? null : 'Nenhuma devolução no período.'}
      altura={260}
    />
  );
}

function CancelamentosNoTempo({ d }: { d: Devolucoes }) {
  const blocos = agrupar(
    d.cancelamentosPorDia.map((x) => ({
      dia: x.dia,
      faturamento: x.itens,
      cupons: x.cupons,
      itens: 0,
    })),
  );
  const total = d.cancelamentosPorDia.reduce((s, x) => s + x.itens + x.cupons, 0);
  const pior = [...blocos].sort((a, b) => b.faturamento - a.faturamento)[0];
  return (
    <CartaoDeGrafico
      id="cv-canc-tempo"
      titulo="Cancelamentos no tempo"
      numero={`${fmt(total, 'inteiro')} cancelamentos`}
      conclusao={
        pior && pior.faturamento ? (
          <>
            Mais cancelamentos em <strong>{pior.rotulo}</strong> ({fmt(pior.faturamento, 'inteiro')}{' '}
            itens). A linha são os cupons cancelados inteiros.
          </>
        ) : (
          'Nenhum cancelamento no período.'
        )
      }
      modelos={MODELOS_TEMPO}
      padrao="combo"
      desenhar={(m) =>
        serieNoTempo(m, {
          cats: blocos.map((b) => b.rotulo),
          atual: blocos.map((b) => b.faturamento),
          anterior: blocos.map(() => null),
          nome: 'Itens cancelados',
          formato: 'inteiro',
          linha: {
            nome: 'Cupons inteiros',
            valores: blocos.map((b) => b.cupons),
            formato: 'inteiro',
          },
        })
      }
      fonte="Fonte: cancelamentos da API do HOS"
      vazio={total ? null : 'Nenhum cancelamento no período.'}
      altura={260}
    />
  );
}

function PorBalconista({
  id,
  titulo,
  grupos,
  formato,
  valor,
  frase,
}: {
  id: string;
  titulo: string;
  grupos: Devolucoes['devolucoesPorBalconista'];
  formato: 'reais' | 'inteiro';
  valor: (g: Devolucoes['devolucoesPorBalconista'][number]) => number;
  frase: (g: Devolucoes['devolucoesPorBalconista'][number], total: number) => ReactNode;
}) {
  const top = [...grupos].sort((a, b) => valor(b) - valor(a)).slice(0, 10);
  const total = grupos.reduce((s, g) => s + valor(g), 0);
  return (
    <CartaoDeGrafico
      id={id}
      titulo={titulo}
      numero={top[0] ? `${primeiroNome(top[0].nome)} · ${fmt(valor(top[0]), formato)}` : '—'}
      conclusao={top[0] && total ? frase(top[0], total) : 'Nada no período.'}
      modelos={MODELOS_PARTES}
      padrao="barras"
      desenhar={(m) =>
        partes(m, {
          itens: top.map((g) => ({ nome: primeiroNome(g.nome), valor: valor(g) })),
          formato,
        })
      }
      fonte="Fonte: API do HOS"
      vazio={top.length ? null : 'Nada no período.'}
      altura={260}
    />
  );
}

function LinhaDaDevolucao({ x }: { x: Devolucoes['devolucoes'][number] }) {
  return (
    <aside className="cv-cartao cv-lado">
      <h2 className="cv-h2 cv-h2-topo">Cupom {x.cupom}</h2>
      <p className="cv-cartao-sub">o que a API do HOS conta sobre esta devolução</p>
      <ul className="cv-tempo">
        <li>
          <i className="cv-tempo-venda" />
          <div>
            <strong>venda original · cupom {x.cupom}</strong>
            <span>
              vendeu: {x.balconista} · {x.produto}
            </span>
          </div>
        </li>
        <li>
          <i className="cv-tempo-devol" />
          <div>
            <strong>{diaCurto(x.dia)} · devolução</strong>
            <span>
              {menos(x.valor)} · {x.quantidade} un
              {x.recebidaPor ? ` · recebeu: ${x.recebidaPor}` : ''} · motivo não informado pela
              fonte
            </span>
          </div>
        </li>
        <li>
          <i />
          <div>
            <strong>
              {x.descontoNaPremiacao ? 'item deduzido do cheque' : 'sem desconto na premiação'}
            </strong>
            <span>
              {x.descontoNaPremiacao
                ? `${menos(x.descontoNaPremiacao)} · aparece como "devolvido" no comprovante da semana`
                : 'o item devolvido não tinha prêmio lançado nessa semana'}
            </span>
          </div>
        </li>
      </ul>
    </aside>
  );
}
